import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Loader,
  Pagination,
  PaginationWrapper,
} from "@braintrust/braintrust-ui-components";
import { SaveOutlinedIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useAccountType } from "@js/apps/common/hooks";
import { FreelancerActionBar } from "@js/apps/freelancer/components/freelancer-action-bar";
import { WithJobActionsContext } from "@js/apps/jobs/components/job-actions/with-job-actions";
import { JobsListing } from "@js/apps/jobs/components/listing";
import { JobLocationContext } from "@js/apps/jobs/context";
import { RouterLink } from "@js/components/link";
import { NavigateWithSnackbarToastReceiver } from "@js/components/snackbar";
import { useQueryParams } from "@js/hooks";
import { AppLayout } from "@js/layouts/app";

import { Header, SavedJobFiltersDrawer } from "../../components";
import { JobsListFilters } from "../../components/jobs-list-filters";
import type { UseJobListingArg } from "../../hooks";
import { useJobListing } from "../../hooks";

import styles from "./style.module.scss";

export type JobsListingPageProps = {
  bgcolor?: string;
} & Partial<UseJobListingArg>;

export const JobsListingPage = ({
  defaultFilters,
  location = JobLocationContext.Values.job_listing,
  bgcolor = "soft-blue",
}: JobsListingPageProps) => {
  const { isFreelancer } = useAccountType();

  const {
    jobs,
    loading,
    total,
    openingsCount,
    filters,
    filtersFromParams,
    isAnyFilterApplied,
    loadNewFilters,
    refetchJobs,
  } = useJobListing({ defaultFilters, location });

  const isLoading = loading;

  const query = useQueryParams();
  const showSavedSearchesDrawerParam =
    query["show_saved_searches_drawer"] === "true";
  const [savedFiltersDrawerOpen, setSavedFiltersDrawerOpen] = useState(false);

  const setSavedFiltersDrawerOpenHandler = () => {
    setSavedFiltersDrawerOpen(true);
  };

  useEffect(() => {
    if (showSavedSearchesDrawerParam) setSavedFiltersDrawerOpenHandler();
  }, [showSavedSearchesDrawerParam]);

  const withJobActionContextProviderValue = useMemo(
    () => ({
      afterAction: refetchJobs,
    }),
    [refetchJobs],
  );

  return (
    <AppLayout pageTitle="Jobs" className="wider" bgcolor={`var(--${bgcolor})`}>
      <SavedJobFiltersDrawer
        location={location}
        onChange={loadNewFilters}
        open={savedFiltersDrawerOpen}
        setOpen={setSavedFiltersDrawerOpen}
      />
      <div className={styles.listingPage}>
        {isFreelancer && <FreelancerActionBar />}
        <JobLocationContext.Provider value={location}>
          <Header
            buttons={[
              <Button
                key="Saved searches"
                variant="primary"
                startIcon={<SaveOutlinedIcon style={{ fontSize: "17px" }} />}
                onClick={setSavedFiltersDrawerOpenHandler}
              >
                Saved searches
              </Button>,
            ]}
            filtersSocket={
              <JobsListFilters
                openingsCount={openingsCount}
                defaultRole={defaultFilters?.role}
                filters={filters}
                filtersFromParams={filtersFromParams}
                isAnyFilterApplied={isAnyFilterApplied}
                location={location}
                refetchJobs={refetchJobs}
              />
            }
          />
          <WithJobActionsContext.Provider
            value={withJobActionContextProviderValue}
          >
            <div className={styles.listingPageContent}>
              {isLoading ? (
                <Loader centered />
              ) : (
                <>
                  <NavigateWithSnackbarToastReceiver />
                  <JobsListing jobs={jobs} searchResults={isAnyFilterApplied} />
                  <PaginationWrapper sx={{ mt: 0 }}>
                    <Pagination
                      RouterLink={RouterLink}
                      count={total}
                      perPage={SETTINGS.JOBS_LISTING_JOBS_PER_PAGE}
                    />
                  </PaginationWrapper>
                </>
              )}
            </div>
          </WithJobActionsContext.Provider>
        </JobLocationContext.Provider>
      </div>
    </AppLayout>
  );
};
